/**
 * Parse free-form date strings into a LocalDate object.
 * <p/>
 * The string can be parsed with or without date separators. When it contains separators, the parts
 * are expected to be in the correct order for the user's current locale.
 * <p/>
 * <p>
 * If the string contains separators, it must contains 2 and only 2 separators
 * </p>
 * <p>
 * The 3 date part values must be valid numbers and be in the proper range for the part.
 * </p>
 * The following operations are done on the string before parsing begins:
 * 1. trim()
 * 2. toLowerCase()
 * 3. remove all whitespace
 * <p/>
 * valid formats
 * -------------
 * MDYY (Assume leading zero was left off of first two numbers)
 * MDDYY (Assume leading zero was left off)
 * MMDDYY
 * MDDYYYY
 * MMDDYYYY
 * M-D-Y
 * the month part must be 1 - 12.
 * the day part must be 1-31 (based on the month.
 * the year part can be either a 2-digit or 4-digit year.
 */
import { getEnvironment } from '../environment/Environment';
import { LocalDate } from '@js-joda/core';
import { removeNonPrintableCharacters } from '../utilities/stringUtilities';

const separators = ['/', '-', '.'];
const validChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '/', '.', '-'];

export const parseDate = (value: string): LocalDate => {
   const dateStr = value.trim().toLowerCase().replace(" ", "");

   if (dateStr.length < 4 || dateStr.length > 10) {
      throw new Error(`The string '${dateStr}' is not a valid date.`);
   }

   checkValidCharactersThrowsException(dateStr);

   const datePartsOrder = getDatePartsOrder();
   let numberSeparators = 0;

   for (let x = 0; x < dateStr.length; x++) {
      const ch = dateStr[x];

      for (let i = 0; i < separators.length; i++) {
         if (ch === separators[i]) {
            numberSeparators++;
         }
      }
   }

   if (numberSeparators > 2) {
      throw new Error(`The string '${dateStr}' is not a valid date.`);
   }

   if (numberSeparators > 0) {
      const dateParts = dateStr.split(/[\\/\\.\\-]/g);

      return toDate(dateParts, datePartsOrder);
   }

   if (dateStr.length === 4) {
      const dateParts = datePartsOrder[0] === 'Y' ? [dateStr.substring(0, 2),
                                                     dateStr.substring(2, 3),
                                                     dateStr.substring(3, 4)]
                                                  : [dateStr.substring(0, 1),
                                                     dateStr.substring(1, 2),
                                                     dateStr.substring(2, 4)];

      return toDate(dateParts, datePartsOrder);
   }

   if (dateStr.length === 5) {
      const dateParts = datePartsOrder[0] === 'Y' ? [dateStr.substring(0, 2),
                                                     dateStr.substring(2, 3),
                                                     dateStr.substring(3, 5)]
                                                  : [dateStr.substring(0, 1),
                                                     dateStr.substring(1, 3),
                                                     dateStr.substring(3, 5)];

      return toDate(dateParts, datePartsOrder);
   }

   if (dateStr.length === 6) {
      const dateParts = [dateStr.substring(0, 2),
                         dateStr.substring(2, 4),
                         dateStr.substring(4, 6)];

      return toDate(dateParts, datePartsOrder);
   }

   if (dateStr.length === 7) {
      const dateParts = datePartsOrder[0] === 'Y' ? [dateStr.substring(0, 4),
                                                     dateStr.substring(4, 5),
                                                     dateStr.substring(5, 7)]
                                                  : [dateStr.substring(0, 1),
                                                     dateStr.substring(1, 3),
                                                     dateStr.substring(3, 7)];

      return toDate(dateParts, datePartsOrder);
   }

   const dateParts = datePartsOrder[0] === 'Y' ? [dateStr.substring(0, 4),
                                                  dateStr.substring(4, 6),
                                                  dateStr.substring(6, 8)]
                                               : [dateStr.substring(0, 2),
                                                  dateStr.substring(2, 4),
                                                  dateStr.substring(4, 8)];

   return toDate(dateParts, datePartsOrder);
};

const checkValidCharactersThrowsException = (dateStr: string) => {
   for (let i = 0; i < dateStr.length; i++) {
      const ch = dateStr.charAt(i);

      let found = false;

      for (let index = 0; index < validChars.length; index++) {
         if (ch === validChars[index]) {
            found = true;
            break;
         }
      }

      if (!found) {
         throw new Error(`The character '${ch}' is not allowed in a date.`);
      }
   }
};

const getDatePartsOrder = (): string => {
   const locale = getEnvironment().locale;
   const dateFormatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      timeZone: 'UTC'
   });

   const dateStr = dateFormatter.format(new Date(Date.UTC(2018, 11, 31)));

   return removeNonPrintableCharacters(dateStr.replace('12', 'M')
                                        .replace('31', 'D')
                                        .replace('2018', 'Y')
                                        .replace(/[\\/\\.\\-]/g, ''));
};

const toDate = (dateParts: string[], datePartsOrder: string): LocalDate => {
   let year = 0;
   let month = 0;
   let day = 0;

   for (let i = 0; i < 3; i++) {
      if (datePartsOrder[i] === 'M') {
         month = parseInt(dateParts[i], 10);
      }

      if (datePartsOrder[i] === 'D') {
         day = parseInt(dateParts[i], 10);
      }

      if (datePartsOrder[i] === 'Y') {
         year = parseInt(dateParts[i], 10);
      }
   }

   if (year < 1000) {
      if (year <= currentYear() % 100 + 30) {
         year += 2000;
      }
      else {
         year += 1900;
      }
   }

   return LocalDate.of(year, month, day);
};

const currentYear = (): number => new Date().getFullYear();
